import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Send, Phone, Mail, MapPin, Loader2 } from "lucide-react";

const schema = z.object({
  vorname: z.string().min(2, "Bitte Vornamen angeben"),
  nachname: z.string().min(2, "Bitte Nachnamen angeben"),
  email: z.string().email("Bitte gültige E-Mail-Adresse angeben"),
  phone: z.string().optional(),
  message: z.string().min(5, "Bitte beschreiben Sie kurz Ihr Anliegen"),
  consent: z.literal(true, {
    errorMap: () => ({ message: "Bitte Datenschutzhinweis bestätigen" }),
  }),
});

export default function Kontakt() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { vorname: "", nachname: "", email: "", phone: "", message: "", consent: false },
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const { consent, vorname, nachname, phone, message, email } = data;
      const body = new URLSearchParams({
        "form-name": "contact",
        vorname,
        nachname,
        email,
        phone: phone || "",
        message,
      });
      const res = await fetch("/__forms.html", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (!res.ok) throw new Error("Network error");
      navigate("/danke");
    } catch (e) {
      toast.error("Senden fehlgeschlagen", {
        description: "Bitte versuchen Sie es später erneut oder rufen Sie uns an.",
      });
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-zinc-300 px-4 py-4 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-colors";

  return (
    <section
      id="kontakt"
      data-testid="kontakt-section"
      className="relative bg-zinc-950 text-white py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: heading + contact info */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-12 h-px bg-red-500" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-red-500">
                Kontakt
              </span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tighter leading-[0.95]">
              Lassen Sie uns<br />
              gemeinsam<br />
              <span className="text-red-500">Bauen.</span>
            </h2>
            <p className="mt-8 text-zinc-400 font-light text-base md:text-lg max-w-md">
              Haben Sie Fragen oder möchten Sie ein Projekt mit uns besprechen?
              Kontaktieren Sie uns gerne – wir stehen Ihnen für alle Anliegen rund um
              Ihre Bauvorhaben zur Verfügung.
            </p>

            <div className="mt-12 space-y-6">
              {[
                { Icon: Phone, label: "Telefon", value: "0201 43355633", href: "tel:+4920143355633" },
                { Icon: Mail, label: "E-Mail", value: "info@honexbau.de", href: "mailto:info@honexbau.de" },
                { Icon: MapPin, label: "Adresse", value: "Max-Keith-Str. 66\n45136 Essen" },
              ].map(({ Icon, label, value, href }) => (
                <div key={label} className="flex items-start gap-4">
                  <span className="w-10 h-10 bg-red-600 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-white" />
                  </span>
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-bold">
                      {label}
                    </div>
                    {href ? (
                      <a
                        href={href}
                        data-testid={`contact-${label.toLowerCase()}`}
                        className="text-white hover:text-red-500 transition-colors font-medium"
                      >
                        {value}
                      </a>
                    ) : (
                      <div className="text-white font-medium whitespace-pre-line">{value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit(onSubmit)}
              data-testid="contact-form"
              name="contact"
              data-netlify="true"
              netlify-honeypot="bot-field"
              className="bg-white text-zinc-900 p-6 md:p-10"
              noValidate
            >
              <input type="hidden" name="form-name" value="contact" />
              <p style={{ display: "none" }}>
                <label>Nicht ausfüllen: <input name="bot-field" /></label>
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-700 mb-2">
                    Vorname *
                  </label>
                  <input
                    type="text"
                    data-testid="contact-vorname"
                    placeholder="Max"
                    {...register("vorname")}
                    className={inputCls}
                  />
                  {errors.vorname && (
                    <p className="mt-1 text-xs text-red-600">{errors.vorname.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-700 mb-2">
                    Nachname *
                  </label>
                  <input
                    type="text"
                    data-testid="contact-nachname"
                    placeholder="Mustermann"
                    {...register("nachname")}
                    className={inputCls}
                  />
                  {errors.nachname && (
                    <p className="mt-1 text-xs text-red-600">{errors.nachname.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-700 mb-2">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    data-testid="contact-email"
                    placeholder="name@firma.de"
                    {...register("email")}
                    className={inputCls}
                  />
                  {errors.email && (
                    <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-700 mb-2">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    data-testid="contact-phone"
                    placeholder="+49 …"
                    {...register("phone")}
                    className={inputCls}
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-700 mb-2">
                  Nachricht *
                </label>
                <textarea
                  rows={6}
                  data-testid="contact-message"
                  placeholder="Beschreiben Sie kurz Ihr Vorhaben, Ort und gewünschten Zeitrahmen …"
                  {...register("message")}
                  className={`${inputCls} resize-none`}
                />
                {errors.message && (
                  <p className="mt-1 text-xs text-red-600">{errors.message.message}</p>
                )}
              </div>

              <label className="mt-5 flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="contact-consent"
                  {...register("consent")}
                  className="mt-1 accent-red-600 w-4 h-4 flex-shrink-0"
                />
                <span className="text-xs text-zinc-600 leading-relaxed">
                  Ich habe die <Link to="/datenschutz" className="underline hover:text-red-600">Datenschutzhinweise</Link> gelesen
                  und stimme der Verarbeitung meiner Daten zur Bearbeitung der Anfrage zu.
                </span>
              </label>
              {errors.consent && (
                <p className="mt-1 text-xs text-red-600">{errors.consent.message}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="mt-8 group w-full inline-flex items-center justify-center gap-3 bg-zinc-950 text-white px-8 py-5 text-sm font-bold uppercase tracking-widest hover:bg-red-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Wird gesendet …
                  </>
                ) : (
                  <>
                    Anfrage senden
                    <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
